import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import {
  AMOUNT_PRESETS,
  DEFAULT_AMOUNT,
  DEFAULT_MONTHS,
  FUNDER_RATES,
  MAX_AMOUNT,
  MAX_MONTHS,
  MIN_AMOUNT,
  MIN_MONTHS,
  REPAYMENT_CAP,
  payoff,
} from '@/data/funderRates';
import shared from '@/styles/public.module.css';
import styles from './OfferEstimator.module.css';

const digitsOnly = (value) => parseInt(String(value).replace(/[^0-9]/g, ''), 10) || 0;
const group = (n) => n.toLocaleString('en-US');
const money = (n) => `$${group(Math.round(n))}`;

/**
 * The signature element of the homepage: four funders pricing the same case
 * differently, sorted by what you would actually repay.
 *
 * Rates come from src/data/funderRates.js and are placeholders — see that file.
 */
const OfferEstimator = () => {
  const navigate = useNavigate();
  // The typed string is the source of truth so the field can be emptied while
  // the user retypes; the numeric amount is derived from it.
  const [amountText, setAmountText] = useState(group(DEFAULT_AMOUNT));
  const [months, setMonths] = useState(DEFAULT_MONTHS);

  const amount = Math.min(Math.max(digitsOnly(amountText), MIN_AMOUNT), MAX_AMOUNT);

  const offers = useMemo(() => {
    const rows = FUNDER_RATES.map((funder) => ({
      ...funder,
      total: payoff(amount, funder.rate, months),
    })).sort((a, b) => a.total - b.total);

    return rows.map((row, i) => ({
      ...row,
      isBest: i === 0,
      // Flag a capped row so its total never reads as an arithmetic bug.
      isCapped: amount > 0 && row.total >= amount * REPAYMENT_CAP - 0.5,
    }));
  }, [amount, months]);

  const spread = offers.length
    ? offers[offers.length - 1].total - offers[0].total
    : 0;

  const handleAmountChange = (event) => {
    const next = Math.min(digitsOnly(event.target.value), MAX_AMOUNT);
    setAmountText(next ? group(next) : '');
  };

  const handleAmountBlur = () => {
    if (digitsOnly(amountText) === 0) setAmountText(group(DEFAULT_AMOUNT));
  };

  // Same-page jump on the homepage, where the ledger section lives; falls back
  // to a route change anywhere else the estimator is ever reused.
  const goToLedger = (event) => {
    event.preventDefault();
    const target = document.getElementById('open');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else navigate('/#open');
  };

  const monthsLabel = `${months} ${months === 1 ? 'month' : 'months'}`;
  const pct = ((months - MIN_MONTHS) / (MAX_MONTHS - MIN_MONTHS)) * 100;

  return (
    <div className={styles.panel} data-testid="offer-estimator">
      <div className={styles.head}>
        <h2 className={styles.title}>How much do you need?</h2>

        <label className={styles.label} htmlFor="estimator-amount">
          Amount
        </label>
        <div className={styles.amountRow}>
          <span className={styles.currency} aria-hidden="true">
            $
          </span>
          <input
            id="estimator-amount"
            className={`${styles.amountInput} mcl-num`}
            type="text"
            inputMode="numeric"
            value={amountText}
            onChange={handleAmountChange}
            onBlur={handleAmountBlur}
            aria-label="Amount you need in dollars"
            data-testid="estimator-amount"
          />
        </div>

        <div className={styles.presets}>
          {AMOUNT_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              className={styles.preset}
              aria-pressed={amount === preset}
              onClick={() => setAmountText(group(preset))}
              data-testid={`estimator-preset-${preset}`}
            >
              ${group(preset)}
            </button>
          ))}
        </div>

        <div className={styles.monthsHead}>
          <label className={styles.label} htmlFor="estimator-months">
            When do you expect to settle?
          </label>
          <span className={`${styles.monthsValue} mcl-num`}>{monthsLabel}</span>
        </div>
        <input
          id="estimator-months"
          className={styles.slider}
          type="range"
          min={MIN_MONTHS}
          max={MAX_MONTHS}
          step={1}
          value={months}
          onChange={(e) => setMonths(Number(e.target.value))}
          aria-label="Months until you expect to settle"
          aria-valuetext={monthsLabel}
          style={{ '--pct': `${pct}%` }}
          data-testid="estimator-months"
        />
      </div>

      <div className={styles.body}>
        <div className={styles.offersHead}>
          <span className={styles.offersLabel}>Example offers</span>
          <span className={styles.live}>
            <i className={styles.liveDot} aria-hidden="true" />
            {`${FUNDER_RATES.length} FUNDERS BIDDING`}
          </span>
        </div>

        {/* Totals change as the inputs move, so screen readers are told. */}
        <div aria-live="polite" data-testid="estimator-offers">
          {offers.map((offer) => (
            <div
              key={offer.name}
              className={`${styles.offer} ${
                offer.isBest ? styles.offerBest : styles.offerDim
              }`}
            >
              <div>
                <div className={styles.offerName}>
                  {offer.name}
                  {offer.isBest && <span className={styles.tag}>LOWEST COST</span>}
                </div>
                <div className={`${styles.offerRate} mcl-num`}>
                  {`${(offer.rate * 100).toFixed(1)}% simple annual${
                    offer.isCapped ? ` · at ${REPAYMENT_CAP}× cap` : ''
                  }`}
                </div>
              </div>
              <div className={`${styles.offerTotal} mcl-num`}>
                {money(offer.total)}
                <small>YOU REPAY</small>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.savings}>
          <p>Picking the best offer instead of the worst one keeps</p>
          <strong className={`${styles.savingsFigure} mcl-num`} data-testid="estimator-savings">
            {`${money(spread)} in your pocket`}
          </strong>
        </div>

        <button
          type="button"
          className={`${shared.btn} ${shared.btnAction} ${shared.btnLg}`}
          onClick={() => navigate('/apply')}
          data-testid="estimator-cta"
        >
          See my real offers
        </button>

        <p className={styles.fine}>
          Illustration only. Real offers depend on your case, your state, and each
          funder&rsquo;s review. This is not a loan — repayment comes out of your
          settlement, and only if you win.
        </p>

        {/* Deliberately just the section's own label, with no summary of what it
            says. The "how we make money" claims are legally sensitive and pending
            review, so this is navigation, not a restatement of them. */}
        <a href="/#open" className={styles.fineLink} onClick={goToLedger}>
          How we make money
          <ArrowRight size={13} aria-hidden="true" />
        </a>
      </div>
    </div>
  );
};

export default OfferEstimator;
